import * as SentryReact from '@sentry/react';

import { resolveNativePlatform } from '@/lib/capacitor';
import { config, isSentryEnabled } from '@/lib/config';

let activeSdk = SentryReact;

function sanitizeEvent(event) {
  event.user = undefined;
  event.request = event.request ? {} : undefined;
  return event;
}

function sanitizeTransaction(event) {
  event.transaction = 'application';
  event.spans = [];
  return event;
}

function sentryOptions(
  { dsn, environment, release, tracesSampleRate },
  runtime,
) {
  return {
    dsn,
    enabled: true,
    environment,
    release,
    sendDefaultPii: false,
    tracesSampleRate,
    initialScope: { tags: { runtime: runtime.platform } },
    beforeSend: sanitizeEvent,
    beforeSendTransaction: sanitizeTransaction,
    ignoreErrors: [
      'ResizeObserver loop',
      'Network request failed',
      'Load failed',
    ],
  };
}

async function loadNativeSentry() {
  return import('@sentry/capacitor');
}

export function createSentryInitializer({
  nativePlatform = resolveNativePlatform,
  webSdk = SentryReact,
  loadNative = loadNativeSentry,
  activate = (sdk) => {
    activeSdk = sdk;
  },
} = {}) {
  let initialized = false;

  return {
    async initialize(options) {
      if (initialized || !options.enabled || !options.dsn) return false;

      try {
        const runtime = nativePlatform();
        const sdkOptions = sentryOptions(options, runtime);
        if (runtime.native && !runtime.preview) {
          const nativeSdk = await loadNative();
          nativeSdk.init(sdkOptions, webSdk.init);
          activate(nativeSdk);
        } else {
          webSdk.init(sdkOptions);
          activate(webSdk);
        }
        initialized = true;
        return true;
      } catch {
        return false;
      }
    },
  };
}

const initializer = createSentryInitializer();

export const initializeSentry = () =>
  initializer.initialize({
    dsn: config.sentry.dsn,
    enabled: isSentryEnabled,
    environment: config.sentry.environment,
    release: config.app.version,
    tracesSampleRate: config.sentry.tracesSampleRate,
  });

const Sentry = {
  addBreadcrumb: (...args) => activeSdk.addBreadcrumb(...args),
  captureException: (...args) => activeSdk.captureException(...args),
};

export default Sentry;
